import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  __DEFAULT_CHANNEL__,
  __DEFAULT_PLATFORM__,
  STORAGE_CHANNEL_KEY,
  STORAGE_OPEN_ID_KEY,
  STORAGE_PLATFORM_KEY,
  STORAGE_SW_TOKEN_KEY,
  STORAGE_TOKEN_KEY,
} from '@os-web/const';
import { closeToast, showToast } from 'vant';
import 'vant/es/toast/style';

import { getFingerId } from './src/identity.ts';

type RequestData = Record<string, unknown>;

export interface HttpRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  raw?: boolean;
  toast?: boolean | number;
}

export type HttpInstance = Omit<AxiosInstance, 'get'> & {
  get<T = unknown>(url: string, params?: unknown, config?: HttpRequestConfig): Promise<T>;
};

function isObject(value: unknown): value is RequestData {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function getStorage(key: string) {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(key) || '';
}

function setHeaderIfMissing(
  headers: InternalAxiosRequestConfig['headers'],
  key: string,
  value?: string,
) {
  if (!value || headers.has(key)) return;
  headers.set(key, value);
}

async function setRequestHeaders(headers: InternalAxiosRequestConfig['headers']) {
  const token = getStorage(STORAGE_TOKEN_KEY);
  const swToken = getStorage(STORAGE_SW_TOKEN_KEY);
  const fingerId = await getFingerId();

  setHeaderIfMissing(headers, 'Authorization', token);
  setHeaderIfMissing(headers, 'Yqw-Token', token);
  setHeaderIfMissing(headers, 'platform', getStorage(STORAGE_PLATFORM_KEY) || __DEFAULT_PLATFORM__);
  setHeaderIfMissing(headers, 'channel', getStorage(STORAGE_CHANNEL_KEY) || __DEFAULT_CHANNEL__);
  setHeaderIfMissing(headers, 'BrowserIdentSalt', fingerId);
  setHeaderIfMissing(headers, 'VisitorUniqueId', fingerId);
  setHeaderIfMissing(headers, 'Access-Control-Allow-Origin', '*');
  setHeaderIfMissing(headers, 'token', swToken);
}

function hasFile(data: unknown) {
  if (!isObject(data)) return false;

  return Object.values(data).some((value) => {
    return value instanceof FileList || value instanceof File || value instanceof Blob;
  });
}

function setDefaultOpenId(data: unknown) {
  if (!isObject(data)) return data;

  const openId = getStorage(STORAGE_OPEN_ID_KEY);
  if (openId && !data.open_id) {
    data.open_id = openId;
  }

  return data;
}

function getToastDuration(toast: boolean | number | undefined) {
  return typeof toast === 'number' ? toast : 1500;
}

function getErrorMessage(data: unknown, fallback: string) {
  if (!isObject(data)) return fallback;

  const message = data.msg || data.message;
  return typeof message === 'string' ? message : fallback;
}

function createHttpOS() {
  const instance = axios.create({
    timeout: 60 * 1000,
  }) as HttpInstance;

  instance.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

  instance.interceptors.request.use(async (config) => {
    const method = config.method?.toLowerCase();
    const requestConfig = config as InternalAxiosRequestConfig & HttpRequestConfig;

    await setRequestHeaders(config.headers);

    if (method === 'post') {
      requestConfig.data = setDefaultOpenId(requestConfig.data ?? {});
      if (hasFile(requestConfig.data)) {
        config.headers.set('Content-Type', 'multipart/form-data');
      }
    }

    requestConfig.toast ??= false;
    requestConfig.raw ??= false;

    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => {
      const { data, status } = response;
      const config = response.config as InternalAxiosRequestConfig & HttpRequestConfig;
      const code = isObject(data) ? data.code : undefined;

      if (String(status).startsWith('2') && [1, 200].includes(Number(code))) {
        return config.raw || !isObject(data) ? data : data.data;
      }

      if ([1007, 1010, 3303].includes(Number(code))) {
        localStorage.removeItem(STORAGE_TOKEN_KEY);
        showToast({
          duration: getToastDuration(config.toast),
          message: 'Not logged in',
        });
        return Promise.reject(data);
      }

      if (config.toast) {
        closeToast();
        showToast({
          duration: getToastDuration(config.toast),
          message: getErrorMessage(data, '请稍后重试'),
        });
      }

      return Promise.reject(data);
    },
    (error: AxiosError) => {
      const config = error.config as HttpRequestConfig | undefined;

      if (config?.toast && !axios.isCancel(error)) {
        showToast({
          duration: getToastDuration(config.toast),
          message: error.message || '网络异常',
        });
      }

      return Promise.reject(error);
    },
  );

  const originGet = instance.get.bind(instance);
  instance.get = <T = unknown>(url: string, params?: unknown, config?: HttpRequestConfig) => {
    return originGet<T>(url, {
      ...config,
      params,
    });
  };

  return instance;
}

const http = createHttpOS();

export function setHeader(key: string, value: string) {
  http.defaults.headers[key] = value;
}

export default http;
